import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ApproveGroupRequestsResponse,
  GroupRequestApprovedEnvelope,
  GroupRequestCreatedEnvelope,
  GroupRequestQuery,
  GroupRequestRejectedEnvelope,
  GroupRequestsQueryBuilder,
  ListGroupRequestsOptions,
  ListGroupRequestsResponse,
  QueryGroupRequestsOptions,
  QueryGroupRequestsResponse,
  RejectGroupRequestsResponse,
  Rejection,
  approveGroupRequests as universalApproveGroupRequests,
  listGroupRequests as universalListGroupRequests,
  queryGroupRequests as universalQueryGroupRequests,
  rejectGroupRequests as universalRejectGroupRequests,
  typedQueryGroupRequests as universalTypedQueryGroupRequests,
} from './social-groups-v2-group-request-group-requests.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/groups' };

export function approveGroupRequests(
  httpClient: HttpClient
): ApproveGroupRequestsSignature {
  return (groupRequestIds: string[]) =>
    universalApproveGroupRequests(
      groupRequestIds,
      // @ts-ignore
      { httpClient }
    );
}

interface ApproveGroupRequestsSignature {
  /**
   * Approves group requests.
   *
   * Only Wix users can approve group requests.
   * @param - Group request IDs to approve. Limited to `100`.
   */
  (groupRequestIds: string[]): Promise<
    NonNullablePaths<
      ApproveGroupRequestsResponse,
      | `groupRequest`
      | `groupRequest.${number}.group.privacyStatus`
      | `groupRequest.${number}.group.settings.allowedToInviteMembers`
      | `groupRequest.${number}.group.settings.allowedToApproveJoinRequests`
      | `groupRequest.${number}.status`,
      6
    >
  >;
}

export function rejectGroupRequests(
  httpClient: HttpClient
): RejectGroupRequestsSignature {
  return (rejections: Rejection[]) =>
    universalRejectGroupRequests(
      rejections,
      // @ts-ignore
      { httpClient }
    );
}

interface RejectGroupRequestsSignature {
  /**
   * Rejects group requests.
   *
   * Only Wix users can reject group requests.
   * @param - Rejection data.
   */
  (rejections: Rejection[]): Promise<
    NonNullablePaths<
      RejectGroupRequestsResponse,
      | `groupRequest`
      | `groupRequest.${number}.group.privacyStatus`
      | `groupRequest.${number}.group.settings.allowedToInviteMembers`
      | `groupRequest.${number}.group.settings.allowedToApproveJoinRequests`
      | `groupRequest.${number}.status`,
      6
    >
  >;
}

export function listGroupRequests(
  httpClient: HttpClient
): ListGroupRequestsSignature {
  return (options?: ListGroupRequestsOptions) =>
    universalListGroupRequests(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListGroupRequestsSignature {
  /**
   * Lists requests to create a group.
   *
   * The `listGroupRequests()` function returns a Promise that resolves to a list of up to 1,000 create group requests on your site.
   *
   * Using the options parameter, you can filter your list of posts, set the amount of posts to be returned, and sort your list in a specified order.
   *
   * By default, the list is sorted by `_createdDate` in descending order. Only admins can see create group requests. Members can access their own create group requests in their site.
   *
   * >**Note:** This function is only relevant if admin approval is required for creating a group.
   *
   * @param - Paging options.
   */
  (options?: ListGroupRequestsOptions): Promise<
    NonNullablePaths<
      ListGroupRequestsResponse,
      | `groupRequests`
      | `groupRequests.${number}.group.privacyStatus`
      | `groupRequests.${number}.group.settings.allowedToInviteMembers`
      | `groupRequests.${number}.group.settings.allowedToApproveJoinRequests`
      | `groupRequests.${number}.status`,
      6
    >
  >;
}

export function queryGroupRequests(
  httpClient: HttpClient
): QueryGroupRequestsSignature {
  return (options?: QueryGroupRequestsOptions) =>
    universalQueryGroupRequests(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryGroupRequestsSignature {
  /**
   * Creates a query to retrieve a list of create requests.
   *
   * The `queryGroupRequests()` function builds a query to retrieve a list of all requests to create a group, and returns a `GroupRequestsQueryBuilder` object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](/group-requests-query-builder/find) function. You can refine the query by chaining `GroupRequestQueryBuilder` functions onto the query. `GroupRequestQueryBuilder` functions enable you to sort, filter, and control the results that `queryGroupRequests()` returns.
   *
   * `queryGroupRequests()` runs with these `GroupRequestsQueryBuilder` defaults, which you can override:
   * - [`limit(1000)`](/group-requests-query-builder/limit)
   *
   * The following `GroupRequestsQueryBuilder` functions are supported for `queryGroupRequests()`. For a full description of the `GroupRequests` object, see the object returned for the [`items`](/group-requests-query-result/items) property in `GroupRequestsQueryResult`.
   */
  (options?: QueryGroupRequestsOptions): GroupRequestsQueryBuilder;
}

export function typedQueryGroupRequests(
  httpClient: HttpClient
): TypedQueryGroupRequestsSignature {
  return (query: GroupRequestQuery, options?: QueryGroupRequestsOptions) =>
    universalTypedQueryGroupRequests(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryGroupRequestsSignature {
  /** */
  (query: GroupRequestQuery, options?: QueryGroupRequestsOptions): Promise<
    NonNullablePaths<
      QueryGroupRequestsResponse,
      | `groupRequests`
      | `groupRequests.${number}.group.privacyStatus`
      | `groupRequests.${number}.group.settings.allowedToInviteMembers`
      | `groupRequests.${number}.group.settings.allowedToApproveJoinRequests`
      | `groupRequests.${number}.status`,
      6
    >
  >;
}

export const onGroupRequestApproved = EventDefinition(
  'wix.social_groups.v2.group_request_approved',
  true,
  (event: GroupRequestApprovedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.groupRequest.group.createdDate' },
            { path: 'data.groupRequest.group.updatedDate' },
            { path: 'data.groupRequest.group.recentActivityDate' },
            { path: 'data.groupRequest.group.lastActivityDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.groupRequest.group.details.logoPosition.x' },
            { path: 'data.groupRequest.group.details.logoPosition.y' },
            { path: 'data.groupRequest.group.details.mobileLogoPosition.x' },
            { path: 'data.groupRequest.group.details.mobileLogoPosition.y' },
            { path: 'data.groupRequest.group.coverImage.position.x' },
            { path: 'data.groupRequest.group.coverImage.position.y' },
            { path: 'data.groupRequest.group.coverImage.mobilePosition.x' },
            { path: 'data.groupRequest.group.coverImage.mobilePosition.y' },
          ],
        },
      ])
    )
)<GroupRequestApprovedEnvelope>();
export const onGroupRequestCreated = EventDefinition(
  'wix.social_groups.v2.group_request_created',
  true,
  (event: GroupRequestCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.group.createdDate' },
            { path: 'entity.group.updatedDate' },
            { path: 'entity.group.recentActivityDate' },
            { path: 'entity.group.lastActivityDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.group.details.logoPosition.x' },
            { path: 'entity.group.details.logoPosition.y' },
            { path: 'entity.group.details.mobileLogoPosition.x' },
            { path: 'entity.group.details.mobileLogoPosition.y' },
            { path: 'entity.group.coverImage.position.x' },
            { path: 'entity.group.coverImage.position.y' },
            { path: 'entity.group.coverImage.mobilePosition.x' },
            { path: 'entity.group.coverImage.mobilePosition.y' },
          ],
        },
      ])
    )
)<GroupRequestCreatedEnvelope>();
export const onGroupRequestRejected = EventDefinition(
  'wix.social_groups.v2.group_request_rejected',
  true,
  (event: GroupRequestRejectedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.groupRequest.group.createdDate' },
            { path: 'data.groupRequest.group.updatedDate' },
            { path: 'data.groupRequest.group.recentActivityDate' },
            { path: 'data.groupRequest.group.lastActivityDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.groupRequest.group.details.logoPosition.x' },
            { path: 'data.groupRequest.group.details.logoPosition.y' },
            { path: 'data.groupRequest.group.details.mobileLogoPosition.x' },
            { path: 'data.groupRequest.group.details.mobileLogoPosition.y' },
            { path: 'data.groupRequest.group.coverImage.position.x' },
            { path: 'data.groupRequest.group.coverImage.position.y' },
            { path: 'data.groupRequest.group.coverImage.mobilePosition.x' },
            { path: 'data.groupRequest.group.coverImage.mobilePosition.y' },
          ],
        },
      ])
    )
)<GroupRequestRejectedEnvelope>();

export {
  AccessRestriction,
  AccessRestrictionDataOneOf,
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AllowPolicy,
  ApproveGroupRequestsRequest,
  ApproveGroupRequestsResponse,
  BaseEventMetadata,
  CancelGroupRequestRequest,
  CancelGroupRequestResponse,
  ContentType,
  CoverImage,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Events,
  Group,
  GroupDetails,
  GroupDetailsPosition,
  GroupRequest,
  GroupRequestApproved,
  GroupRequestApprovedEnvelope,
  GroupRequestCreatedEnvelope,
  GroupRequestQuerySpec,
  GroupRequestRejected,
  GroupRequestRejectedEnvelope,
  GroupRequestsQueryBuilder,
  GroupRequestsQueryResult,
  GroupSettings,
  IdentificationData,
  IdentificationDataIdOneOf,
  Identity,
  IdentityType,
  Image,
  ItemsToUpdate,
  ListGroupRequestsOptions,
  ListGroupRequestsRequest,
  ListGroupRequestsResponse,
  Logo,
  MessageEnvelope,
  OnboardingStepSettings,
  OwnershipFilter,
  Paging,
  PagingMetadata,
  Position,
  PrivacyStatus,
  Query,
  QueryGroupRequestsOptions,
  QueryGroupRequestsRequest,
  QueryGroupRequestsResponse,
  RejectGroupRequestsRequest,
  RejectGroupRequestsResponse,
  Rejection,
  RequestDetails,
  RequestStatus,
  RestoreInfo,
  SortOrder,
  Sorting,
  StepKey,
  SubmitGroupRequestRequest,
  SubmitGroupRequestResponse,
  Type,
  WebhookIdentityType,
  utils,
} from './social-groups-v2-group-request-group-requests.universal.js';
